// A job's parts on order (jobs.partsOnOrder, F-24): what came in and what's still expected, in
// words, and when everything is in, so the office books the return visit. Quantities only: the
// query carries no costs, as a dispatcher reads it.

import { partLineWords } from '@flowology/ui-web/words';
import { PO_STATUS } from '../inventory/orders-model.ts';
import { qtyText } from '../inventory/model.ts';

export interface PartsLine {
  description: string;
  ordered: string;
  received: string;
  cancelled: string;
  backOrdered: boolean;
}
export interface PartsOrder {
  poNumber: string;
  status: string;
  vendorName: string;
  lines: PartsLine[];
}

const k = (s: string) => Math.round(Number(s) * 1000);
/** Still expected on a line: ordered, less received and cancelled. */
export const stillExpected = (l: PartsLine) =>
  Math.max(0, k(l.ordered) - k(l.received) - k(l.cancelled)) / 1000;

/** Where a line stands, in the job page's words: "1 of 3 in, 2 cancelled", "0 of 2 in, the rest back-ordered". */
export function partsLineStands(l: PartsLine): string {
  const rest = stillExpected(l);
  return partLineWords({
    received: qtyText(l.received),
    ordered: qtyText(l.ordered),
    cancelled: k(l.cancelled) > 0 ? qtyText(l.cancelled) : null,
    expected: rest > 0 ? qtyText(rest) : null,
    backOrdered: l.backOrdered,
  });
}

/** "Toilet flapper: 1 of 2 in, the rest back-ordered". */
export const partsLineText = (l: PartsLine): string => `${l.description}: ${partsLineStands(l)}`;

export const partsStatus = (o: PartsOrder) => PO_STATUS[o.status] ?? o.status;

/** What's on order: a cancelled PO brings nothing (its credit note is the books'), so it isn't listed. */
export const partsShown = (orders: readonly PartsOrder[]) => orders.filter((o) => o.status !== 'cancelled');

/**
 * Every part ordered for the job is in: something was received and nothing is still expected (a
 * cancelled line expects a credit note, not a delivery). A draft that was never sent isn't "in".
 */
export function partsAllIn(orders: readonly PartsOrder[]): boolean {
  const live = orders.filter((o) => o.status !== 'cancelled');
  return (
    live.length > 0 &&
    live.every(
      (o) => o.status !== 'draft' && o.status !== 'sending' && o.lines.every((l) => stillExpected(l) === 0),
    ) &&
    live.some((o) => o.lines.some((l) => k(l.received) > 0))
  );
}

/** "Parts arrived: PO-6 is in." / "Parts arrived: PO-6 and PO-7 are in." */
export function partsArrivedText(orders: readonly PartsOrder[]): string {
  const n = orders.filter((o) => o.status !== 'cancelled').map((o) => o.poNumber);
  const list = n.length > 1 ? `${n.slice(0, -1).join(', ')} and ${n.at(-1)}` : (n[0] ?? '');
  return `Parts arrived: ${list} ${n.length > 1 ? 'are' : 'is'} in.`;
}
